import { useEffect, useMemo, useState } from "react";
import { fetchGfAssets } from "../../../../api/giftingClient";
import {
  PLATFORM_LABEL,
  fmtYmd,
  reuseScopeLabels,
} from "../../../../lib/giftingRules";
import type { GfAsset, GfBoardResponse, GfEntry } from "../../../../types/gifting";
import { GfAvatar, GfCard, GfEmpty, GfTag } from "../../ui/GfUi";
import { cardInfo, type PipelineCtx } from "./model";

/** アーカイブ（段階8と中止）。投稿・2次利用・完了日。許諾の用途/期限は素材庫 API から補う（読めなければ件数のみ） */
export function ArchiveView({
  entries,
  ctx,
  board,
  selectedId,
  onOpen,
}: {
  entries: GfEntry[];
  ctx: PipelineCtx;
  board: GfBoardResponse;
  selectedId: string | null;
  onOpen: (dealId: string) => void;
}) {
  const [assets, setAssets] = useState<GfAsset[] | null>(null);
  useEffect(() => {
    let alive = true;
    fetchGfAssets("all").then(
      (a) => {
        if (alive) setAssets(a);
      },
      () => {
        if (alive) setAssets(null);
      }
    );
    return () => {
      alive = false;
    };
  }, [board]);

  const reuseByDeal = useMemo(() => {
    const m = new Map<string, GfAsset[]>();
    for (const a of assets ?? []) {
      if (a.reuse_allowed !== 1) continue;
      const list = m.get(a.deal_id) ?? [];
      list.push(a);
      m.set(a.deal_id, list);
    }
    return m;
  }, [assets]);

  const rows = [...entries].sort((a, b) => (doneAt(b) > doneAt(a) ? 1 : -1));

  return (
    <GfCard flush>
      <div className="gf-table-wrap">
        <div className="gf-av">
          <div className="gf-av-row gf-av-head">
            <span>クリエイター</span>
            <span>案件</span>
            <span>投稿</span>
            <span>2次利用</span>
            <span>完了日</span>
          </div>
          {rows.length === 0 && <GfEmpty>アーカイブされた案件はまだありません</GfEmpty>}
          {rows.map((e) => {
            const info = cardInfo(e, ctx);
            const cancelled = e.status === "cancelled";
            const granted = reuseByDeal.get(e.id) ?? [];
            let reuse = "未取得";
            let reuseOk = false;
            if (cancelled) reuse = "—";
            else if (info.campaign && info.campaign.reuse_required === 0) reuse = "対象外";
            else if (e.reuse_allowed_count > 0) {
              reuseOk = true;
              const first = granted[0];
              const scopes = first ? reuseScopeLabels(first.reuse_scope).join("・") : "";
              const until = first?.reuse_until ? `／〜${fmtYmd(first.reuse_until)}` : "";
              reuse = `許諾済${first ? `（${scopes || "用途未設定"}${until}）` : `（${e.reuse_allowed_count}件）`}`;
            }
            const lp = e.latest_post;
            return (
              <button
                type="button"
                key={e.id}
                className={`gf-av-row gf-av-body${selectedId === e.id ? " is-selected" : ""}`}
                onClick={() => onOpen(e.id)}
              >
                <span className="gf-lv-who">
                  <GfAvatar name={info.creatorName} size={28} />
                  <span className="gf-lv-who-text">
                    <span className="gf-lv-name">{info.creatorName}</span>
                    <span className="gf-lv-handle">{info.handle}</span>
                  </span>
                </span>
                <span className="gf-lv-deal">{info.campaignName}</span>
                <span className="gf-av-post">
                  {lp ? `${PLATFORM_LABEL[lp.platform]}${lp.post_type ? ` ${lp.post_type}` : ""}` : "—"}
                  {e.post_count > 1 && <span className="gf-av-more"> 他{e.post_count - 1}件</span>}
                </span>
                <span className={`gf-av-reuse${reuseOk ? " is-ok" : ""}`}>{reuse}</span>
                <span className="gf-av-done">
                  {cancelled && <GfTag tone="warn">中止</GfTag>}
                  <span className="gf-mono">{shortDate(doneAt(e))}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </GfCard>
  );
}

function doneAt(e: GfEntry): string {
  return e.completed_at ?? e.stage_changed_at ?? e.updated_at;
}

function shortDate(s: string): string {
  const ymd = fmtYmd(s); // YYYY-MM-DD
  return ymd ? `${Number(ymd.slice(5, 7))}/${ymd.slice(8, 10)}` : "—";
}
